import type { CashIntent } from '@tenant/shared-ui'

export interface CashTenant {
  id: string
  slug: string
  primaryDomain: string | null
  brand?: { name?: { az?: string | null; ru?: string | null; en?: string | null } | null } | null
}
export interface CashUser { id: string; name: string; email: string; role: string; isActive: boolean; storeId?: string }
export interface CashSession {
  tenantId: string
  host: string
  user: CashUser
  token: string
  /** Stable only for this principal's unresolved work and same-owner reauthentication. */
  binding: string
  /** Response fence; never used as the durable receipt address. */
  revision: string
}

const listeners = new Set<() => void>()
const prefix = (tenantId: string, host = window.location.host) => `cash:v1:${JSON.stringify([host, tenantId])}`
const sessionKey = (tenantId: string, host = window.location.host) => `${prefix(tenantId, host)}:session`
const intentKey = (session: CashSession) => `${prefix(session.tenantId, session.host)}:${JSON.stringify([session.user.id, session.binding])}:intent`
const notify = () => listeners.forEach(listener => listener())
let observing = false
export function listenSession(listener: () => void) {
  if (!observing) {
    window.addEventListener('storage', event => { if (event.key === null || event.key.startsWith('cash:v1:')) notify() })
    observing = true
  }
  listeners.add(listener)
  return () => { listeners.delete(listener) }
}
export function readSession(tenant: Pick<CashTenant, 'id'>): CashSession | null {
  try {
    const value = JSON.parse(localStorage.getItem(sessionKey(tenant.id)) || 'null') as CashSession | null
    return value && value.tenantId === tenant.id && value.host === window.location.host &&
      typeof value.token === 'string' && value.token.length > 20 && typeof value.binding === 'string' &&
      typeof value.revision === 'string' && typeof value.user?.id === 'string' && typeof value.user.name === 'string' ? value : null
  } catch { return null }
}
export function sessionIsCurrent(session: CashSession): boolean {
  if (session.host !== window.location.host) return false
  const stored = readSession({ id: session.tenantId })
  return stored?.user.id === session.user.id && stored.binding === session.binding &&
    stored.revision === session.revision && stored.token === session.token
}
export function writeSession(session: CashSession): void {
  if (session.host !== window.location.host) throw new Error('Sessiya dəyişib.')
  localStorage.setItem(sessionKey(session.tenantId, session.host), JSON.stringify(session))
  if (!sessionIsCurrent(session)) throw new Error('Sessiya bu cihazda saxlanılmadı.')
  notify()
}
function intentRecords(session: CashSession): Array<{ storageKey: string; intent: CashIntent; savedAt: number }> {
  const base = intentKey(session), rows: Array<{ storageKey: string; intent: CashIntent; savedAt: number }> = []
  for (let index = 0; index < localStorage.length; index++) {
    const key = localStorage.key(index)
    if (key !== base && !key?.startsWith(base + ':')) continue
    const value = JSON.parse(localStorage.getItem(key!) || 'null') as (CashIntent & { savedAt?: number }) | null
    if (value && typeof value.command?.key === 'string' && typeof value.command.type === 'string' &&
      value.command.payload && typeof value.command.payload === 'object' && typeof value.uncertain === 'boolean')
      rows.push({ storageKey: key!, intent: value, savedAt: value.savedAt || 0 })
  }
  return rows.sort((a, b) => a.savedAt - b.savedAt || a.storageKey.localeCompare(b.storageKey))
}
export function readIntent(session: CashSession): CashIntent | null {
  try { return intentRecords(session)[0]?.intent || null } catch { return null }
}
export function saveIntent(session: CashSession, intent: CashIntent): void {
  if (!sessionIsCurrent(session)) throw new Error('Sessiya dəyişib.')
  const before = intentRecords(session), existing = before.find(row => row.intent.command.key === intent.command.key)
  if (existing && JSON.stringify(existing.intent.command) !== JSON.stringify(intent.command)) throw new Error('İlkin əməliyyatın məlumatlarını dəyişmək olmaz.')
  if (!existing && before.length) throw new Error('Əvvəl göndərilmiş əməliyyatın nəticəsini yoxlayın.')
  // Each attempt owns its own immutable address: competing windows can never
  // overwrite an already dispatched operation's recovery reference.
  const key = existing?.storageKey || `${intentKey(session)}:${intent.command.key}`
  localStorage.setItem(key, JSON.stringify({ ...intent, savedAt: existing?.savedAt || Date.now() }))
  const after = intentRecords(session)
  if (!existing && after.some(row => row.intent.command.key !== intent.command.key)) {
    localStorage.removeItem(key)
    throw new Error('Əməliyyat başqa pəncərədə başladılıb. Əvvəl onun nəticəsini yoxlayın.')
  }
  if (!after.some(row => row.intent.command.key === intent.command.key)) throw new Error('Əməliyyatın bərpa nömrəsi saxlanılmadı.')
}
export function clearIntent(session: CashSession, expectedKey: string): void {
  if (!sessionIsCurrent(session)) return
  for (const row of intentRecords(session)) if (row.intent.command.key === expectedKey) localStorage.removeItem(row.storageKey)
}
export function removeSession(session: CashSession): void {
  if (!sessionIsCurrent(session)) return
  if (intentRecords(session).length) throw new Error('Əvvəl əməliyyatın nəticəsini yoxlayın.')
  localStorage.removeItem(sessionKey(session.tenantId, session.host))
  notify()
}
/** Cold-launch/update guard exposes only a boolean for this app's current host. */
export function hasPendingWork(tenant?: Pick<CashTenant, 'id'>): boolean {
  try {
    if (tenant) { const session = readSession(tenant); return Boolean(session && intentRecords(session).length) }
    const ownPrefix = `cash:v1:[${JSON.stringify(window.location.host)},`
    for (let index = 0; index < localStorage.length; index++) {
      const key = localStorage.key(index)
      if (!key?.startsWith(ownPrefix) || !key.endsWith(':session')) continue
      const candidate = JSON.parse(localStorage.getItem(key) || 'null') as CashSession | null
      if (candidate?.host !== window.location.host || typeof candidate.tenantId !== 'string') continue
      const session = readSession({ id: candidate.tenantId })
      if (session && intentRecords(session).length) return true
    }
    return false
  } catch { return true }
}
